import { countBy } from 'lodash';
import groupBy from 'lodash/groupBy';

import { deliveryServiceNameMap } from '@/domain/models/DeliveryService';
import { KPIStats } from '@/domain/models/KPI';
import { Item, Order, OrderState } from '@/domain/models/Order';

import { DateDomainService } from '..';

interface Dictionary<T> {
  [index: string]: T;
}

export class OrderDomainService {
  // [TODO]
  // backendから渡されるsales_priceという項目をtotalPriceとして扱う。
  // タブレット側と一致させるために、Uberのプロモーション注文の時はorder_total_priceを入れないといけない。
  // しかしフロントでロジックを持つのは良くないので全てbackendに寄せる。下記は取り急ぎの対応
  static totalPrice(order: Order): number {
    if (
      order.delivery_type === '自社配達' ||
      order.delivery_service_name === deliveryServiceNameMap['CamelOrder']
    ) {
      return (
        (order.tax_include_item_total_price ?? 0) + (order.delivery_fee ?? 0)
      );
    }

    return order.tax_include_item_total_price ?? 0;
  }

  static toKPIStats(orders: Order[]): KPIStats {
    const counts = countBy(orders, (o) => o.order_state) as {
      [key in OrderState]?: number;
    };
    const stats = orders.reduce(
      (result: KPIStats, o) => {
        const totalPrice = result.totalPrice + this.totalPrice(o);
        const orderCount = result.orderCount + 1;

        return { ...result, totalPrice, orderCount };
      },
      {
        totalPrice: 0,
        orderCount: 0,
        finishedOrderCount: counts['finished'] || 0,
        canceledOrderCount: counts['canceled'] || 0,
      }
    );
    return stats;
  }

  static toItemList(orders: Order[]): Item[] {
    return orders.reduce(
      (result: Item[], order) => [...result, ...order.items],
      []
    );
  }

  static createDictionaryByDate(orders: Order[]): Dictionary<Order[]> {
    return this.orderGroupByDateDomain(orders, 'format');
  }

  static createDictionaryByDayOfTheWeek(orders: Order[]): Dictionary<Order[]> {
    return this.orderGroupByDateDomain(orders, 'getDay');
  }

  static createDictionaryByHourOfTheDay(orders: Order[]): Dictionary<Order[]> {
    return this.orderGroupByDateDomain(orders, 'getHour');
  }

  static createDictionaryByStoreName(orders: Order[]): Dictionary<Order[]> {
    return this.orderGroupByKey(orders, 'store_name');
  }

  static createDictionaryByDeliveryServiceName(
    orders: Order[]
  ): Dictionary<Order[]> {
    return this.orderGroupByKey(orders, 'delivery_service_name');
  }

  static createDictionaryByBrandName(orders: Order[]): Dictionary<Order[]> {
    return this.orderGroupByKey(orders, 'brand_name');
  }

  static createDictionaryByDeliveryType(orders: Order[]): Dictionary<Order[]> {
    return this.orderGroupByKey(orders, 'delivery_type');
  }

  static createDictionaryByAuthStore(orders: Order[]): Dictionary<Order[]> {
    return this.orderGroupByKey(orders, 'auth_store_name');
  }

  private static orderGroupByDateDomain(
    orders: Order[],
    method: 'format' | 'getDay' | 'getHour'
  ): Dictionary<Order[]> {
    return groupBy(orders, (order) => {
      return DateDomainService[method](new Date(order.order_date_and_time));
    });
  }

  private static orderGroupByKey(
    orders: Order[],
    targetKey:
      | 'store_name'
      | 'delivery_service_name'
      | 'brand_name'
      | 'delivery_type'
      | 'auth_store_name'
  ): Dictionary<Order[]> {
    return groupBy(orders, (order) => {
      return order[targetKey];
    });
  }
}
